/**
 * Client-side analytics event helpers for Meta Pixel (fbq) and Google
 * Analytics 4 (gtag).
 *
 * These are thin wrappers over the `window.fbq` / `window.gtag` globals that
 * GTM injects (via its Meta Pixel + GA4 tags). Every helper:
 *   - guards `typeof window` (safe to import in SSR/RSC graphs), and
 *   - no-ops when the relevant global is absent (non-production, ad-blocked,
 *     or the script hasn't loaded yet).
 *
 * They never throw and never block a user action. Call them ALONGSIDE the
 * existing Plausible `track()` calls — they don't replace them.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Fire a standard Meta Pixel event (`fbq('track', ...)`).
 *
 * When `opts.eventId` is supplied it is passed as `eventID` so the browser
 * Pixel event deduplicates against the matching server-side CAPI event that
 * shares the same `event_id`.
 */
export function metaTrack(
  event: string,
  params?: Record<string, unknown>,
  opts?: { eventId?: string },
): void {
  if (typeof window === "undefined") return;
  window.fbq?.(
    "track",
    event,
    params,
    opts?.eventId ? { eventID: opts.eventId } : undefined,
  );
}

/** Fire a custom Meta Pixel event (`fbq('trackCustom', ...)`). */
export function metaTrackCustom(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  window.fbq?.("trackCustom", event, params);
}

/** Fire a Google Analytics 4 event (`gtag('event', name, params)`). */
export function gaEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.gtag?.("event", name, params);
}
